import { LoginCaptcha } from '../../features/loginCaptcha/runtime/components/index.js';
import { useLoginCaptcha } from '../../features/loginCaptcha/runtime/hooks/index.js';
import { useFormik } from "formik";
import React, { useContext, useState } from "react";
import * as Yup from "yup";

import { useAuth } from "@/context/AuthContext";
import useSiteSettings from "@/hooks/useSiteSettings";
import { openTelegramContact } from "@/utils/telegramContact";
import { showErrorToast, showSuccessToast } from "@/utils/toastUtils";
import { AppContext } from "../../context/AppContext";
import AuthNoticeModal from "./AuthNoticeModal";

const getLoginMissingMessage = (values) => {
  if (!String(values.user_unique_id || "").trim() && !String(values.password || "").trim()) {
    return "아이디와 비밀번호를 입력해주세요.";
  }
  if (!String(values.user_unique_id || "").trim()) return "아이디를 입력해주세요.";
  if (!String(values.password || "").trim()) return "비밀번호를 입력해주세요.";
  return "";
};

const Login = ({ onSuccess }) => {
  const { signIn } = useAuth();
  const captcha = useLoginCaptcha(true);
  const { updateLoggedIn } = useContext(AppContext);
  const { settings } = useSiteSettings();
  const [error, setError] = useState("");
  const [noticeMessage, setNoticeMessage] = useState("");

  const validationSchema = Yup.object().shape({
    user_unique_id: Yup.string().required("아이디를 입력해주세요."),
    password: Yup.string().required("비밀번호를 입력해주세요."),
  });

  const formik = useFormik({
    initialValues: {
      user_unique_id: "",
      password: "",
    },
    validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        setError("");
        const completed = await captcha.submit(async (token) => {
          await signIn(values.user_unique_id, values.password, token);
          return true;
        });
        if (!completed) return;
        updateLoggedIn(true);
        showSuccessToast("로그인되었습니다.");
        onSuccess?.();
      } catch (submitError) {
        const message =
          submitError.message || "로그인에 실패했습니다. 아이디와 비밀번호를 확인해주세요.";
        setError(message);
        setNoticeMessage(message);
        showErrorToast(message);
      } finally {
        setSubmitting(false);
      }
    },
  });

  const isFieldInvalid = (name) => formik.touched[name] && formik.errors[name];

  const handleLoginSubmit = (event) => {
    event.preventDefault();

    const missingMessage = getLoginMissingMessage(formik.values);
    if (missingMessage) {
      formik.setTouched({ user_unique_id: true, password: true }, true);
      setNoticeMessage(missingMessage);
      return;
    }

    if (captcha.canSubmit && !formik.isSubmitting) formik.submitForm();
  };

  const handlePasswordFind = () => {
    openTelegramContact(settings, () => {
      setNoticeMessage("텔레그램 문의 링크가 준비되지 않았습니다.");
    });
  };

  return (
    <form className="auth-form" onSubmit={handleLoginSubmit} noValidate>
      <AuthNoticeModal message={noticeMessage} onClose={() => setNoticeMessage("")} />

      <div className="form-group">
        <label htmlFor="login_user_unique_id" className="form-label">
          아이디 <span className="required">*</span>
        </label>
        <input
          type="text"
          id="login_user_unique_id"
          name="user_unique_id"
          className="form-input"
          value={formik.values.user_unique_id}
          onBlur={formik.handleBlur}
          onChange={formik.handleChange}
          placeholder="아이디"
          autoComplete="username"
        />
        {isFieldInvalid("user_unique_id") ? (
          <div className="error-message">{formik.errors.user_unique_id}</div>
        ) : null}
      </div>

      <div className="form-group">
        <label htmlFor="login_password" className="form-label">
          비밀번호 <span className="required">*</span>
        </label>
        <input
          type="password"
          id="login_password"
          name="password"
          className="form-input"
          value={formik.values.password}
          onBlur={formik.handleBlur}
          onChange={formik.handleChange}
          placeholder="비밀번호"
          autoComplete="current-password"
        />
        {isFieldInvalid("password") ? (
          <div className="error-message">{formik.errors.password}</div>
        ) : null}
      </div>

      {error ? <div className="error-message">{error}</div> : null}

      <LoginCaptcha captcha={captcha} />
      <button type="submit" className="register-button" disabled={formik.isSubmitting || !captcha.canSubmit}>
        {formik.isSubmitting ? (
          <>
            <span className="auth-spinner" aria-hidden="true" />
            로그인 중
          </>
        ) : (
          "로그인"
        )}
      </button>

      <div className="auth-switch-row">
        <button type="button" className="auth-switch-button" onClick={handlePasswordFind}>
          비밀번호 찾기
        </button>
      </div>
    </form>
  );
};

export default Login;
